import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { Place } from '@app/modules/model/place';
import { Ticket } from '@app/modules/model/ticket';
import { TicketMixedPayment } from '@app/modules/model/ticketmixed';
import { AskWaiter } from '@app/modules/model/askwaiter';
import { Discount } from '@app/modules/model/discount';

import { AppConstants } from '@app/app.constants';
import { alertService } from '@app/modules/common/alert';
import { pickPaymentTerminal, placeBlocksCash, resolvePaymentTerminals, isPaymentTerminalManual } from '@app/components/model/ticket/ui-paytef/ui-paytef-utils';
import { languageSupport, languageService } from '@app/modules/common/language';
import { toastService } from '@app/modules/common/toast';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';
import { deviceService } from '@app/modules/sockets';

import { UiCashPayModal } from '@app/components/model/ticket/ui-cash/ui-cash';
import { UiCashGuardCashPayModal } from '@app/components/model/ticket/ui-cashguard-cash/ui-cashguard-cash';
import { cashlessRefundStore } from '@app/components/model/ticket/ui-cashguard-cash/cashless-refund-store';
import { tryCashlessCardPayment, chooseCardTerminal, CashlessModalDeps } from '@app/components/model/ticket/ui-cashguard-cash/ui-cashless-utils';
import { UiCardPayModal } from '@app/components/model/ticket/ui-card/ui-card';
import { UiPaytefPayModal } from '@app/components/model/ticket/ui-paytef/ui-paytef';
import { UiToPrintertModal } from '@app/components/login/modal/print/ui-toprinter-modal';
import { isPaidPaylineLocked } from './is-paid-payline-locked';

@Component({
    selector: 'ui-ticket-view',
    templateUrl:'./ui-view.html',
    styleUrls: [ 
        './ui-view.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiTicketView extends languageSupport implements OnInit, OnChanges, OnDestroy {
    @Input('place') _place: Place = null;
    @Input('ticket') _ticket: Ticket = null;

    private get _user(){
        // Usuario EFECTIVO (fuente única data.EffectiveUser): el camarero del PIN si
        // lo hay, el principal bloqueado bajo bloqueo (deniega), o la sesión. Si no,
        // un camarero con PIN conservaría permisos de owner para editar/cancelar/
        // reabrir/devolver, y BAJO BLOQUEO caería al owner de sesión.
        return this.data.EffectiveUser;
    }

    /***********************************/
    /* DATA COMPONENTS                 */
    /***********************************/

    get CanLast(){
        return (this._place?.ShowLastClosed || false) && (this.LastTickets.length > 0);
    }

    get CashpayMaxPrice(){
        return AppConstants.CashpayMaxPrice;
    }

    get InvoiceMaxPrice(){
        return AppConstants.InvoiceMaxPrice;
    }

    get TicketColor(){
        if (this._ticket.IsCancelled){
            return 'danger';
        }

        if (this._ticket.IsPaid){
            return 'success';
        }

        return this.view.LightTheme ? 'dark' : 'light';
    }
    
    get ButtonColor(){
        return (this.TicketColor == 'light') ? 'dark' : 'light';
    }

    get CanModify(){
        // PD + PAYLINE: solo preparar (V). Editar tras Reabrir.
        // Productos nuevos: comanda nueva desde mesa verde.
        if (isPaidPaylineLocked(this._ticket)){
            return false;
        }
        return this._place.AllowTicketUpdate(this._user);
    }

    get CanDone() {
        return (!this._ticket.IsDirect) && (this._ticket.ProductsToPrepare.length > 0);
    }

    get CanPayment(){
        return (['PAYLINE', 'PAYACCOUNT'].indexOf(this._ticket.payment) == -1);
    }

    get CashLimit(){
        return (this._ticket.price >= this.CashpayMaxPrice);
    }

    /**
     * Slim-device cash lockout — see placeBlocksCash() for the full
     * rationale. CanCash here is a generic "can pay this ticket" gate
     * used by CanCard / CanAccount, so don't fold BlockCash into it;
     * gate the three cash entry points at the template level instead.
     */
    get BlockCash(){
        return placeBlocksCash(this._place, this.view);
    }

    get CanCash() {
        return !this._ticket.IsPaid && this._place.AllowTicketPayment;
    }

    get CanCard() {
        return this._place.AcceptCard && this.CanCash;
    }

    get AllowComanderoCash(){
        return this._place.AllowComanderoCash;
    }

    get AllowComanderoCard(){
        return this._place.AllowComanderoCard;
    }

    get CanMixed(){
        return this.CanCash && this.CanCard && !this.BlockCash;
    }

    get CanAccount(){
        return this._place.AcceptAccount && this.CanCash;
    }

    get CanMerge() {
        return !this._ticket.IsEmpty && (this.MergeTickets.length > 0);
    }

    get CanSplit(){
        return this.HasSelectedProducts && !this.AllSelectedProducts;
    }

    get CanReturn(){
        // Devolución = reembolso: solo aplica a un ticket PAGADO. En una comanda en
        // curso (sin pagar) para quitar productos se edita y se borra (Modificar
        // comanda) — la devolución ahí no tiene sentido (no hay dinero que devolver).
        return this._ticket.IsPaid && this._place.AllowTicketReturn(this._user) && !this._ticket.invceac && ((this._ticket.ProductsToPrepare.length > 0) || (this._ticket.ProductsDelivered.length > 0));
    }

    get CanInvoice(){
        return (this._ticket.account == null) && !this._ticket.ToRefund && this._place.AllowTicketPayment;
    }

    get CanEmail(){
        return (this._ticket.invoice) && (this._ticket.invoice.client.email)
    }

    get CanOpen() {
        return this._place.AllowTicketReopen(this._user) && !this._ticket.ToRefund && !this._ticket.invceac && !this._ticket.invoice;
    }

    get CanCancel(){
        return this._place.AllowTicketCancel(this._user) && !this._ticket.ToRefund && !this._ticket.invceac;
    }

    /***********************************/
    /* STARTS HERE                     */
    /***********************************/

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, private toast: toastService, private modalCtrl: ModalController, private data: dataService, public view: viewService, private devsvc: deviceService, private cashlessStore: cashlessRefundStore) {
        super(lang, change);
    }

    private _subscriptions = [];

    private _user_subscription = null;
    ngOnInit(){
        this._user_subscription = this._user.OnRefresh.subscribe(
        (data) => {
            this.change.markForCheck();
        });    
    }

    ngOnChanges(){
        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];

        if (this._ticket){
            this._subscriptions.push(
                this._ticket.OnRefresh.subscribe(
                data => {
                    if (this._ticket.IsCancelled){
                        this.OnClose();
                    }

                    this.change.markForCheck();
                })
            );

            // prepare the invoice on payment
            if (this._ticket.pendpay){
                this.Action = 'invce';
            }
        }

        if (this._place){
            this._subscriptions.push(
                this._place.OnTargetRefresh('TICKET').subscribe(
                data => {
                    this.change.markForCheck();
                })
            )
        }
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._user_subscription){
            this._user_subscription.unsubscribe();
            this._user_subscription = null;
        }

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];
    }

    @Output('onClose') _onClose = new EventEmitter<any>();
    OnClose(){
        if (this._mergeinfo){
            this.OnMergeCancel();
        }

        this._onClose.emit();
    }

    // Mobile / datáfono only: the back arrow returns to the previous
    // panel (ticket list / table view / cart) without the merge-cancel
    // confirmation. Same semantic as desktop's "click outside the view"
    // — close the visualisation without affecting any pending action.
    OnBack(){
        this._onClose.emit();
    }

    /***********************************/
    /* EVENT HANDLERS                  */
    /***********************************/

    public _action = 'view';
    public _expand = false;

    @Output('onSplit') _onSplit = new EventEmitter<any>();    
    @Output('onAction') _onAction = new EventEmitter<any>();
    @Output('onPanel') _onPanel = new EventEmitter<any>();     

    OnPanel(panel){
        this._onPanel.emit(panel);
    }

    private set Action(value){
        this._action = value;
        switch(this._action){
            case 'view':
                this._expand = false;
                break;
            case 'table':
                this._expand = false;
                break;
            case 'split':
                this._expand = true;
                break;
            case 'merge':
                this._expand = false;
                break;
            case 'retrn':
                this._expand = true;
                break;
            case 'invce':
                this._expand = false;
                break;
        }

        this.change.markForCheck();
    }

    CancelAction(){
        this.Action = 'view';
    }

    private _showOptions = false;
    get ShowOptions(){
        return this._showOptions;
    }

    set ShowOptions(value){
        this._showOptions = value;
        this.change.markForCheck();
    }

    OnTicketOptions(){
        this.ShowOptions = !this.ShowOptions;
    }

    OnViewTable(table){
        this._onPanel.emit({
            scnd: 'table',
            args: {
                qrcode: table
            }
        });
    }

    OnPrepare(){
        this._ticket.status = 'RD';
        if (this._ticket.DoCommit()){
            this._place.DoRefresh('TICKET', true);
        }        
    }

    private _waitmodal = false;
    // Pago mixto: ¿el efectivo entro REALMENTE en el cajon? Solo entonces se
    // puede devolver por CASH_OUT si luego falla la tarjeta. En el fallback a
    // efectivo manual (el modal del cajon fallo y el cajero conto a mano) o sin
    // cajon (paytef puro) el dinero NO esta en la maquina: un CASH_OUT
    // dispensaria saldo ajeno. Lo setea _ProcessMixedCashPayment.
    private _mixedCashInDrawer = false;
    // Anti doble-click de la devolucion: mientras el cajon dispensa/abona
    // (OnTicketRefund es async y espera al datafono), un 2o click reentraria y
    // mandaria otra devolucion. Bloquea el boton mientras esta en curso.
    public _refunding = false;
    private OnPaycash(){
        // Antes tambien exigia IsRecent (<8h): un pedido viejo se marcaba pagado
        // SIN abrir el cajon/datafono. Ahora cualquier ticket sin pagar cobra por
        // la maquina; solo se salta si el local no tiene modal de efectivo.
        if (!this._place.CashModal){
            return true;
        }

        return new Promise<boolean>(async (resolve) => {
            if (!this._waitmodal){
                this._waitmodal = true;
                
                // Resuelve a que CashGuard mandar el cobro (per-ticket flow).
                let _cgInfo = this._place.smartdrawerForTicket(this._ticket);
                let _smartdrawer = _cgInfo?.drawer || null;
                let _cgTill = _cgInfo?.till || null;
                let _tolocal = _cgInfo?.tolocal ?? false;

                let modal;
                if (_smartdrawer && _cgTill){
                    modal = await this.modalCtrl.create({
                        component: UiCashGuardCashPayModal,
                        cssClass: 'modal-cashguard',
                        backdropDismiss: false,
                        componentProps: {
                            tickets: [ this._ticket ],
                            device: _smartdrawer,
                            place: _cgTill,
                            tolocal: _tolocal
                        }
                    });
                } else {
                    // Use regular cash modal (CashGuard not available or failed)
                    modal = await this.modalCtrl.create({
                        component: UiCashPayModal,
                        cssClass: 'modal-cash',
                        backdropDismiss: false,
                        componentProps: { 
                            tickets: [ this._ticket ]
                        }                                    
                    });
                }
    
                modal.onDidDismiss().then(async (detail) => {
                    // Check if CashGuard modal requested fallback to cash payment
                    if (detail.data && detail.data['fallback']){
                        console.log('[SMARTDRAWER] Fallback to cash payment modal requested');
                        // Open regular cash modal as fallback
                        let cashModal = await this.modalCtrl.create({
                            component: UiCashPayModal,
                            cssClass: 'modal-cash',
                            backdropDismiss: false,
                            componentProps: { 
                                tickets: [ this._ticket ]
                            }                                    
                        });
                        
                        cashModal.onDidDismiss().then((cashDetail) => {
                            if (cashDetail.data){
                                this._ticket.given = cashDetail.data['amount'];
                            }
                            resolve(cashDetail.data != null);
                        });
                        
                        await cashModal.present();
                    } else {
                        if (detail.data){
                            this._ticket.given = detail.data['amount'];
                        }
                        resolve(detail.data != null);
                    }
                });
    
    
                await modal.present();
                
                setTimeout(() => {
                    this._waitmodal = false;
                }, 100);
            }
        });
    }

    // Deps que necesita el helper cashless (cobro tarjeta por el datafono del
    // cajon) y que no son globales.
    private _cashlessDeps(amountDue?: number): CashlessModalDeps {
        return {
            modalCtrl: this.modalCtrl,
            cashlessStore: this.cashlessStore,
            hasCardModal: !!this._place.CardModal,
            openManual: (tk) => this._openManualCardModal(tk, amountDue),
            amountDue: amountDue,
            // Liberar el guard al cerrarse la modal cashless (antes del fallback
            // manual), para que _openManualCardModal — que corre dentro del
            // onDidDismiss del helper — no se auto-bloquee con _waitmodal.
            onModalDismissed: () => { this._waitmodal = false; }
        };
    }

    private async OnPaycard(){
        // Antes exigia IsRecent (<8h) y marcaba pagado el ticket viejo SIN pasar
        // por el datafono. Ahora cualquier ticket sin pagar cobra por la maquina;
        // si no hay terminal/CardModal, el flujo de abajo cae a manual igual.

        // Selector de datáfono UNIFICADO (helper compartido): si hay datáfonos de
        // AMBOS tipos (PROSEGUR cashless + PAYTEF por IP) en el place, deja elegir
        // al cajero y enruta por el modelo elegido. Con un solo tipo devuelve
        // 'default' y seguimos el flujo normal de abajo.
        let _cgInfo = this._place.smartdrawerForTicket(this._ticket);
        // Guard anti doble-tap DURANTE el selector: sin esto, dos toques rápidos
        // abrían dos pickers / dos cobros (chooseCardTerminal se espera antes de
        // tomar _waitmodal). Se libera al cerrarse el picker; cada rama de pago
        // vuelve a tomar su propio guard.
        if (this._waitmodal){ return false; }
        this._waitmodal = true;
        let _choice;
        try {
            _choice = await chooseCardTerminal(this.modalCtrl, this.lang, this._place, this.view, _cgInfo);
        } finally {
            this._waitmodal = false;
        }
        if (_choice.kind !== 'default'){
            if (_choice.kind === 'cancelled'){ return false; }
            if (_choice.kind === 'manual'){
                if (!this._place.CardModal){ return true; }
                return this._openManualCardModal([ this._ticket ]);
            }
            if (_choice.kind === 'paytef'){
                return this._openPaytefModal([ this._ticket ], _choice.terminal);
            }
            // prosegur: cobro cashless por el datáfono elegido (inyectado en deps).
            if (this._waitmodal){ return false; }
            let _deps = this._cashlessDeps();
            _deps.chosenTerminal = _choice.terminal;
            this._waitmodal = true;
            let _r: boolean | null;
            try {
                _r = await tryCashlessCardPayment(this.devsvc, _deps, [ this._ticket ], _cgInfo);
            } finally {
                this._waitmodal = false;
            }
            return _r === null ? false : _r;
        }

        // Cajon con datafono integrado (PROSEGUR / CASHLOGY): cobramos la
        // tarjeta por el (CHARGE_CASHLESS) en vez de por paytef. Si el cajon
        // no puede con tarjeta, devuelve null y seguimos al flujo normal.
        //
        // Guard con _waitmodal (el mismo que usan los demas modales) mantenido
        // durante TODO el intento cashless: tryCashlessCardPayment resuelve al
        // cerrarse la modal, asi que un 2o toque mientras se cobra la tarjeta no
        // puede abrir una segunda modal (doble cargo). Se libera antes de caer
        // al flujo paytef, que vuelve a tomar el guard por su cuenta.
        if (this._waitmodal){
            return false;
        }
        this._waitmodal = true;
        let _cgCard: boolean | null;
        try {
            _cgCard = await tryCashlessCardPayment(this.devsvc, this._cashlessDeps(), [ this._ticket ], this._place.smartdrawerForTicket(this._ticket));
        } finally {
            this._waitmodal = false;
        }
        if (_cgCard !== null){
            return _cgCard;
        }

        // Paytef is independent from CardModal: if a terminal is configured
        // we always route the payment through its modal.
        let _terminals = resolvePaymentTerminals(this._place, this.view);

        if (_terminals.length > 0) {
            let _pick = await pickPaymentTerminal(this.modalCtrl, this.lang, _terminals, this.view);
            if (!_pick){
                return false;   // user cancelled the picker
            }
            if (isPaymentTerminalManual(_pick)){
                if (!this._place.CardModal){
                    return true;
                }
                return this._openManualCardModal([ this._ticket ]);
            }
            return this._openPaytefModal([ this._ticket ], _pick);
        }

        if (!this._place.CardModal){
            return true;
        }

        return new Promise<boolean>(async (resolve) => {
            if (!this._waitmodal){
                this._waitmodal = true;
                const modal = await this.modalCtrl.create({
                    component: UiCardPayModal,
                    cssClass: 'modal-card',
                    backdropDismiss: false,
                    componentProps: {
                        tickets: [ this._ticket ]
                    }
                });

                modal.onDidDismiss().then((detail) => {
                    resolve(detail.data);
                });

                await modal.present();

                setTimeout(() => {
                    this._waitmodal = false;
                }, 100);
            }
        })
    }

    private async _openPaytefModal(tickets, terminal, saleAmount?: number): Promise<boolean> {
        return new Promise<boolean>(async (resolve) => {
            if (this._waitmodal){
                resolve(false);   // modal en vuelo: no colgar al caller
                return;
            }
            this._waitmodal = true;
            try {
                let _props: any = {
                    tickets: tickets,
                    terminal: terminal,
                    place: this._place
                };
                if (typeof saleAmount === 'number' && saleAmount > 0){
                    _props.saleAmount = saleAmount;
                }
                const modal = await this.modalCtrl.create({
                    component: UiPaytefPayModal,
                    cssClass: 'modal-paytef',
                    backdropDismiss: false,
                    componentProps: _props
                });

                modal.onDidDismiss().then((detail) => {
                    resolve(detail.data && (detail.data.approved || detail.data.manual) ? true : false);
                });

                await modal.present();
            } catch(_e){
                console.error('[PAY] paytef modal error', _e);
                resolve(false);
            } finally {
                setTimeout(() => { this._waitmodal = false; }, 100);
            }
        });
    }

    private async _openManualCardModal(tickets, saleAmount?: number): Promise<boolean> {
        return new Promise<boolean>(async (resolve) => {
            if (this._waitmodal){
                resolve(false);   // modal en vuelo: no colgar al caller
                return;
            }
            this._waitmodal = true;
            try {
                const modal = await this.modalCtrl.create({
                    component: UiCardPayModal,
                    cssClass: 'modal-card',
                    backdropDismiss: false,
                    componentProps: {
                        tickets: tickets,
                        // Importe explícito (tramo TARJETA de un mixto); sin él el
                        // modal cobraría el total del ticket.
                        saleAmount: saleAmount
                    }
                });

                modal.onDidDismiss().then((detail) => {
                    resolve(!!detail.data);
                });

                await modal.present();
            } catch(_e){
                console.error('[PAY] card modal error', _e);
                resolve(false);
            } finally {
                setTimeout(() => { this._waitmodal = false; }, 100);
            }
        });
    }

    /**
     * Runs the paytef refund leg for OnTicketRefund. Returns:
     *   - true  : refund processed on the terminal, OR user opted for manual refund
     *   - false : no paytef terminal configured / card modal disabled here; caller
     *             continues the normal refund flow silently
     *   - null  : user cancelled mid-way (the refund was NOT processed on the
     *             terminal AND the user did not accept a manual refund) — caller
     *             should abort and leave the ticket as-is
     */
    private async _runPaytefRefundIfAvailable(refundAmount?: number): Promise<boolean | null> {
        // Importe a abonar por tarjeta: el explicito (p.ej. el tramo tarjeta de un
        // mixto) o, por defecto, el total pendiente de devolver (pago con tarjeta
        // puro). Se propaga tambien al abono cashless para que no reembolse de mas.
        let _cardAmount = (typeof refundAmount === 'number' && refundAmount > 0)
            ? refundAmount
            : (Number(this._ticket.ToRefund) || 0);

        // Selector de datáfono para la DEVOLUCIÓN — el MISMO que en el cobro
        // (chooseCardTerminal). Solo aparece si el place tiene datáfonos de AMBOS
        // tipos (PROSEGUR cashless + PAYTEF); con un solo tipo devuelve 'default'
        // y seguimos EXACTAMENTE el flujo de siempre (abono cashless -> paytef).
        // Así, si el cobro fue por PAYTEF en un local con ambos, el operario elige
        // PAYTEF y se abona por SU terminal (no por el datáfono PROSEGUR, que no
        // tendría el cobro mapeado). Espejo del selector de OnPaycard.
        let _cgInfo = this._place.smartdrawerForTicket(this._ticket);
        let _choice = await chooseCardTerminal(this.modalCtrl, this.lang, this._place, this.view, _cgInfo);
        if (_choice.kind === 'cancelled'){
            return null;
        }
        if (_choice.kind === 'manual'){
            return true;    // abono a mano -> sigue el flujo normal de devolucion
        }
        if (_choice.kind === 'paytef'){
            // PAYTEF elegido: al datáfono elegido, sin pasar por el abono del cajón.
            let _wants = await this._askPaytefRefund(_cardAmount);
            if (_wants === null){ return null; }
            if (_wants === false){ return true; }   // manual -> flujo normal
            let _paid = await this._openPaytefRefundModal([ this._ticket ], _choice.terminal, _cardAmount);
            if (!_paid){
                this.toast.ShowAlert('danger', this.lang.tr('@paytef_refund_failed'));
                return null;
            }
            return true;
        }
        if (_choice.kind === 'prosegur'){
            // PROSEGUR elegido: abono por el cajón (CardRefund del datáfono). Si no
            // aplica, NO forzamos PAYTEF (el operario eligió el cajón).
            let _r = await this._runCashlessRefundIfAvailable(refundAmount);
            return _r === false ? false : _r;
        }
        // 'default' (un solo tipo de datáfono en el place): flujo de siempre.

        // Abono cashless: si el ticket se cobro por el datafono WIPAY del
        // Cashlogy (tenemos cacheado su transactionNumber) y el cajon es
        // CASHLOGY, el reembolso va referenciado por el datafono (comando
        // REFUND), no por paytef. false => no aplica, sigue el flujo paytef.
        let _cashlessResult = await this._runCashlessRefundIfAvailable(refundAmount);
        if (_cashlessResult !== false){
            return _cashlessResult;   // true = reembolsado / manual; null = cancelado
        }

        let _terminals = resolvePaymentTerminals(this._place, this.view);
        if (_terminals.length == 0){
            return false;   // no terminal available, caller falls back
        }

        // Ask whether to refund on the terminal — if the card was charged
        // elsewhere or the customer wants cash back, the operator can opt
        // out and the normal refund flow still runs.
        let _refundAmount = _cardAmount;
        let _wantsTerminalRefund = await this._askPaytefRefund(_refundAmount);
        if (_wantsTerminalRefund === null){
            return null;    // user cancelled the alert
        }
        if (_wantsTerminalRefund === false){
            return true;    // user opted for manual refund — continue normal flow
        }

        let _terminal = await pickPaymentTerminal(this.modalCtrl, this.lang, _terminals, this.view);
        if (!_terminal){
            return null;    // user cancelled the terminal picker
        }
        if (isPaymentTerminalManual(_terminal)){
            return true;    // manual refund
        }

        let _ok = await this._openPaytefRefundModal([ this._ticket ], _terminal, _refundAmount);
        if (!_ok){
            // Refund failed or was cancelled on the terminal. Surface the
            // error to the user and abort — do NOT mark the ticket as
            // refunded locally if the terminal did not approve.
            this.toast.ShowAlert('danger', this.lang.tr('@paytef_refund_failed'));
            return null;
        }
        return true;
    }

    private async _askPaytefRefund(amount: number): Promise<boolean | null> {
        return new Promise<boolean | null>((resolve) => {
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@paytef_refund_confirm_title'),
                message: this.lang.tr('@paytef_refund_confirm_message', [ this.Price(amount), this.lang.tr('@currency') ]),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@paytef_refund_confirm_yes'),
                    handler: () => { setTimeout(() => alert.dismiss('yes'), 100); return false; }
                }, {
                    text: this.lang.tr('@paytef_refund_confirm_no'),
                    handler: () => { setTimeout(() => alert.dismiss('no'), 100); return false; }
                }, {
                    text: this.lang.tr('@ticket_paytef_cancel'),
                    role: 'cancel',
                    handler: () => { setTimeout(() => alert.dismiss('cancel'), 100); return false; }
                }]
            });

            alert.onDidDismiss().then((detail: any) => {
                if (detail.data == 'yes') resolve(true);
                else if (detail.data == 'no') resolve(false);
                else resolve(null);
            });

            alert.present();
        });
    }

    private Price(value: number){
        let _v = Math.abs(Number(value) || 0);
        return _v.toFixed(2).replace('.', ',');
    }

    // El CashGuard no dispenso el efectivo de la devolucion: preguntar al
    // operario si entrega el dinero a mano (devolucion manual) o aborta —
    // mismo patron que el "Cobrar manual" del pago. Devuelve true si elige
    // continuar de forma manual, false si aborta.
    private async _askManualRefund(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@cashguard_refund_failed_title'),
                message: this.lang.tr('@cashguard_refund_aborted'),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@cashguard_refund_manual'),
                    handler: () => { setTimeout(() => alert.dismiss('manual'), 100); return false; }
                }, {
                    text: this.lang.tr('@cashguard_abort'),
                    role: 'cancel',
                    handler: () => { setTimeout(() => alert.dismiss('abort'), 100); return false; }
                }]
            });

            alert.onDidDismiss().then((detail: any) => {
                resolve(detail.data == 'manual');
            });

            alert.present();
        });
    }

    private async _openPaytefRefundModal(tickets, terminal, refundAmount: number): Promise<boolean> {
        return new Promise<boolean>(async (resolve) => {
            if (!this._waitmodal){
                this._waitmodal = true;
                const modal = await this.modalCtrl.create({
                    component: UiPaytefPayModal,
                    cssClass: 'modal-paytef',
                    backdropDismiss: false,
                    componentProps: {
                        tickets: tickets,
                        terminal: terminal,
                        place: this._place,
                        mode: 'refund',
                        refundAmount: refundAmount
                    }
                });

                modal.onDidDismiss().then((detail) => {
                    resolve(detail.data && (detail.data.approved || detail.data.manual) ? true : false);
                });

                await modal.present();

                setTimeout(() => {
                    this._waitmodal = false;
                }, 100);
            }
            else {
                resolve(false);
            }
        });
    }

    /**
     * Abono cashless: devolucion referenciada por el datafono WIPAY del
     * Cashlogy (comando REFUND con el transactionNumber del cobro original).
     * Espejo de `_runPaytefRefundIfAvailable` pero por el cajon. Devuelve:
     *   - false : no aplica (sin cobro cashless cacheado / cajon no CASHLOGY /
     *             no alcanzable / nada que devolver) -> sigue el flujo paytef.
     *   - true  : reembolsado en el datafono, o el operario eligio manual.
     *   - null  : el operario cancelo -> abortar, dejar el ticket como esta.
     */
    /**
     * ¿Aplica el abono por el datafono del cajon (CASHLOGY/PROSEGUR) para este
     * ticket? Mismas condiciones que las guardas iniciales de
     * `_runCashlessRefundIfAvailable`, pero SIN abrir dialogos: sirve para
     * decidir en el refund MIXTO si hace falta el picker de paytef. Con un cajon
     * cashless el picker sobra (y cancelarlo abortaba un abono valido).
     */
    // Key ESTABLE del cobro cashless = `_uuid` del ticket (existe desde la
    // creación; el orderno se asigna después, en el commit, así que cachear por
    // orderno perdía la referencia). Fallback a orderno por compatibilidad.
    // Clave de ESCRITURA: el `_uuid` del ticket (disponible al cobrar, cuando
    // orderno aún es null).
    private _cashlessRefKey(): string {
        return (this._ticket as any)._uuid;
    }

    // Clave de LECTURA: `_uuid` es un getter que GENERA uno si no existe
    // (base.ts), así que `_uuid || orderno` nunca probaría el orderno. Para
    // encontrar también las entradas del build anterior (cacheadas por orderno
    // durante el TTL), se prueba primero `_uuid` y, si no hay, el `orderno`.
    private _cashlessLookup(): any {
        return this.cashlessStore.lookup(this._cashlessRefKey())
            || (this._ticket.orderno ? this.cashlessStore.lookup(this._ticket.orderno) : null);
    }

    private _cashlessRefundApplies(): boolean {
        let _cgInfo = this._place.smartdrawerForTicket(this._ticket);
        let _drawer = _cgInfo ? _cgInfo.drawer : null;
        if (!_drawer || (_drawer.model != 'CASHLOGY' && _drawer.model != 'PROSEGUR')){
            return false;
        }
        let _cached = this._cashlessLookup();
        let _orderId = (_cached && _cached.order_id) || null;
        let _txn = (_cached && _cached.transaction_number) || null;
        let _uuid = (this._ticket as any)._uuid || null;
        // CASHLOGY referencia SOLO por transactionNumber (no hay map en el back).
        if (_drawer.model == 'CASHLOGY'){
            return !!_txn;
        }
        // PROSEGUR: orderId cacheado (mismo dispositivo) o `_uuid` del ticket
        // (el backend lo resuelve, cross-device). Mismo criterio que
        // `_runCashlessRefundIfAvailable`.
        return !!(_orderId || _uuid);
    }

    private async _runCashlessRefundIfAvailable(refundAmount?: number): Promise<boolean | null> {
        let _cached = this._cashlessLookup();

        // El cajon del ticket debe ser un cajon cashless (CASHLOGY o PROSEGUR) y
        // estar alcanzable para mandarle el abono por su datafono.
        // smartdrawerForTicket resuelve el till y el transporte (LAN vs WSR)
        // correctos tambien en multi-POS.
        let _cgInfo = this._place.smartdrawerForTicket(this._ticket);
        let _drawer = _cgInfo ? _cgInfo.drawer : null;
        if (!_cgInfo || !_drawer || (_drawer.model != 'CASHLOGY' && _drawer.model != 'PROSEGUR')){
            console.warn('[CASHLESS-REFUND] cajon no cashless (model=', _drawer?.model, ') -> flujo normal');
            return false;
        }

        // Referencias del cobro original. Vienen de la cache LOCAL (mismo
        // dispositivo cobró) o, en PROSEGUR, se resuelven en el BACKEND por el
        // `_uuid` del ticket: CROSS-DEVICE (comandero cobró, TPV devuelve). El
        // print_service guarda un map `_uuid -> orderId` del cobro con tarjeta.
        let _orderId = (_cached && _cached.order_id) || null;
        let _txn = (_cached && _cached.transaction_number) || null;
        let _periId = (_cached && _cached.peripheral_id) || null;
        let _devId = (_cached && _cached.device_id) || null;
        let _uuid = (this._ticket as any)._uuid || null;

        // CASHLOGY referencia SOLO por transactionNumber (no hay map en el back):
        // sin él no se puede abonar por aqui.
        if (_drawer.model == 'CASHLOGY' && !_txn){
            console.warn('[CASHLESS-REFUND] CASHLOGY sin transaction_number cacheado -> flujo normal');
            return false;
        }
        // PROSEGUR: con orderId cacheado (mismo dispositivo) o, si no, con el
        // `_uuid` del ticket (el back lo resuelve). Sin ninguno, no se puede.
        if (_drawer.model == 'PROSEGUR' && !_orderId && !_uuid){
            return false;
        }

        // CROSS-DEVICE = PROSEGUR sin referencia local; el orderId lo resuelve el
        // backend por el `_uuid`. En ese caso NO hay nada local que reservar ni
        // liquidar (el store es de OTRO dispositivo): confiamos en el guard
        // `_waitmodal` + la idempotencia del CardRefund del backend.
        let _crossDevice = _drawer.model == 'PROSEGUR' && !_orderId && !_txn && !!_uuid;
        if (_crossDevice){
            console.warn('[CASHLESS-REFUND] PROSEGUR sin referencia local -> el backend resuelve el orderId por _uuid (cross-device)', _uuid);
        } else {
            console.log('[CASHLESS-REFUND] abono por datafono', _drawer.model, 'ref=', _orderId || _txn);
        }

        // Importe explicito (p.ej. el tramo tarjeta de un mixto); por defecto,
        // el total pendiente de devolver del ticket.
        let _refundAmount = (typeof refundAmount === 'number' && refundAmount > 0)
            ? refundAmount
            : (Number(this._ticket.ToRefund) || 0);
        if (_refundAmount <= 0){
            return false;
        }

        // Confirmar el abono (mismo dialogo que paytef); el operario puede
        // optar por devolucion manual o cancelar.
        let _wants = await this._askPaytefRefund(_refundAmount);
        if (_wants === null){
            return null;    // cancelado
        }
        if (_wants === false){
            return true;    // manual -> sigue el flujo normal de devolucion
        }

        // Reserva anti-doble-abono compartida: SOLO cuando hay referencia LOCAL
        // (mismo dispositivo). La reserva expira sola. En cross-device no hay
        // nada local que reservar; el guard `_waitmodal` (por instancia) + la
        // idempotencia del CardRefund del backend evitan el doble abono.
        let _claimed = false;
        if (_orderId || _txn){
            if (!(await this.cashlessStore.claimCharge(_orderId, _txn))){
                this.toast.ShowAlert('warning', this.lang.tr('@cashless_refund_inprogress'));
                return null;
            }
            _claimed = true;
        }

        let _ok = false;
        try {
            _ok = await this._openCashlessRefundModal(_cgInfo, _refundAmount, _txn, _orderId, _periId, _devId, _uuid);
        }
        finally {
            // Si el abono NO se consumó (fallo/cancelación), liberar la reserva
            // para poder reintentar (solo si la tomamos).
            if (!_ok && _claimed){
                await this.cashlessStore.releaseCharge(_orderId, _txn);
            }
        }
        if (!_ok){
            this.toast.ShowAlert('danger', this.lang.tr('@paytef_refund_failed'));
            return null;
        }

        // Liquidar el abono contra el cargo (solo si había referencia local):
        // descuenta lo abonado del importe restante en TODOS los alias del mismo
        // cargo. En cross-device no hay entrada local que liquidar.
        if (_claimed){
            let _refundedCents = Math.round(_refundAmount * 100);
            this.cashlessStore.settleCharge(_orderId, _txn, _refundedCents);
            await this.cashlessStore.releaseCharge(_orderId, _txn);
        }
        return true;
    }

    private async _openCashlessRefundModal(cgInfo, refundAmount: number, transactionNumber: string | null, orderId: string | null, peripheralId: string | null, deviceId: string | null = null, refundUuid: string | null = null): Promise<boolean> {
        return new Promise<boolean>(async (resolve) => {
            if (!this._waitmodal){
                this._waitmodal = true;
                let _props: any = {
                    tickets: [ this._ticket ],
                    device: cgInfo.drawer,
                    place: cgInfo.till,
                    tolocal: cgInfo.tolocal ?? false,
                    cashless: true,
                    refund: true,
                    amountDue: refundAmount,
                    refundPeripheralId: peripheralId
                };
                // Cada cajon lleva su referencia de abono: CASHLOGY el
                // transactionNumber; PROSEGUR el orderId (MDW / CardRefund).
                if (transactionNumber){ _props.refundTransactionNumber = transactionNumber; }
                if (orderId){ _props.refundOrderId = orderId; }
                // PROSEGUR cross-device: sin orderId local, el _uuid del ticket
                // permite que el backend resuelva el orderId del cobro (map por _uuid).
                if (refundUuid){ _props.refundUuid = refundUuid; }
                // PROSEGUR: enrutar el abono al MISMO datafono del cobro. El
                // modal lo lee en `_deviceid` y _txPayload lo manda como
                // `deviceId`; sin esto el back cae a PaymentTerminal y con 2+
                // datafonos el CardRefund iria al equivocado.
                if (deviceId){ _props.deviceId = deviceId; }
                const modal = await this.modalCtrl.create({
                    component: UiCashGuardCashPayModal,
                    cssClass: 'modal-cashguard',
                    backdropDismiss: false,
                    componentProps: _props
                });

                modal.onDidDismiss().then((detail) => {
                    // Liberar el guard AQUI (al cerrarse la modal), no 100ms tras
                    // present(): asi un 2o click durante el abono no abre una 2a
                    // modal de refund -> evita un 2o CardRefund del mismo cobro.
                    this._waitmodal = false;
                    // Exito = dismiss con data y sin fallback (el success auto-cierra
                    // via AcceptModal). fallback/null = fallo o cancelacion.
                    resolve(!!(detail.data && !detail.data['fallback']));
                });

                try {
                    await modal.present();
                } catch(e){
                    this._waitmodal = false;
                    resolve(false);
                }
            }
            else {
                resolve(false);
            }
        });
    }

    /** Dispensa `amount` € por el CashGuard del till (CASH_OUT). Devuelve true
     *  solo si la maquina confirma SUCCESS. Mismo patron que el dispense de
     *  OnTicketRefund; reutilizado por la devolucion mixta. */
    private async _cashGuardCashOut(cgTill, tolocal: boolean, amount: number): Promise<boolean> {
        try {
            let _host = cgTill.wshost;
            let _port = cgTill.wsport ? parseInt(cgTill.wsport, 10) : 5005;
            let _connection = await this.devsvc.Connect(_host, _port, cgTill.name, tolocal);
            if (!_connection){
                return false;
            }
            // Target del WS = modelo del cajon del till (CASHGUARD/CASHLOGY).
            let _target = cgTill.smartdrawer?.model || 'CASHGUARD';
            try {
                let _status = await _connection.Send(_target, { command: 'STATUS' });
                if (!(_status && _status.exec == 'SUCCESS')){
                    return false;
                }
                let _txId = Math.floor(Math.random() * 1000000);
                let _cents = Math.round(amount * 100);
                let _resp = await _connection.Send(_target, {
                    command: 'CASH_OUT',
                    transaction_id: _txId,
                    amount_due: _cents
                });
                if (_resp && _resp.exec == 'SUCCESS'){
                    this.toast.ShowAlert('success', this.lang.tr('@cashguard_cashout_success'), 2000);
                    return true;
                }
                this.toast.ShowAlert('warning', this.lang.tr('@cashguard_cashout_error'), 3000);
                return false;
            } finally {
                this.devsvc.Disconnect(_connection);
            }
        } catch(e){
            console.error('[CASHGUARD REFUND] cashout error', e);
            return false;
        }
    }

    async OnCommit(payment){
        let _dopay = true;

        if (this._ticket.IsEmpty){
            this._ticket.status = 'CC';
        }
        else {
            if (payment){
                if (payment == 'PAYCASH'){
                    _dopay = await this.OnPaycash();
                }

                if (payment == 'PAYCARD'){
                    _dopay = await this.OnPaycard();
                }

            }  
            else {
                this._ticket.status = 'AC';
            }  
        }
        
        if (_dopay){
            // Cobrar / reabrir / cancelar-si-vacío desde la vista: acción del camarero
            // SELECCIONADO -> sellarlo para la fila e0 del informe de actividad.
            this._ticket.StampWaiter();
            if (this._ticket.ToInvoice){
                this._ticket.pendpay = payment;     // will be paid on invoice
                this.Action = 'invce';
            }
            else {
                this._ticket.payment = payment;
                this._place.DelCart(this._ticket);

                let _commit = await this._ticket.DoCommit();
                if (!_commit){
                    // El cobro NO está en el servidor (lote rechazado o sin dependencias):
                    // no cerrar como si estuviera guardado. Lote rechazado -> el rescate de
                    // dataService lo reintenta; fallo fiscal (firma/alta) -> no se envió nada
                    // y NO se reintenta solo. El operador no debe volver a cobrar.
                    this.toast.ShowAlert('danger', this.lang.tr((this._ticket.LastCommitFailure == 'fiscal') ? '@ticket_commit_fiscal_failed' : '@ticket_commit_failed'), 12000);
                }
                if (_commit){
                    this._onClose.emit();    
                }        
            }
        }
    }

    private _print_pressed_timeout = null;
    private _print_ignore_selected = false;

    private get _SelectPrinter(){
        return this._place.MultiplePOS && (!this._place.IsPOSDevice);
    }

    TicketPrint(){
        if (!this._print_ignore_selected){
            if (this._SelectPrinter){
                this._PrintTicketModal();
            }
            else {  
                this._ticket.DoPrint(false);
            }    

            setTimeout(() => {
                this.ShowOptions = false;
            }, 100);
        }
    }

    private _waittoprintmodal = false;
    private async _PrintTicketModal(){
        this.ShowOptions = false;

        if (!this._waittoprintmodal){            
            this._waittoprintmodal = true;

            const modal = await this.modalCtrl.create({
                component: UiToPrintertModal,
                cssClass: 'ticket-modal',
                componentProps: { 
                    ticket: this._ticket
                }                                    
            });

            await modal.present();
        }

        setTimeout(() => {
            this._waittoprintmodal = false;
        }, 100);
    }

    OnPrintPressed(down){
        if (down){
            if (this._print_pressed_timeout){
                clearTimeout(this._print_pressed_timeout);
                this._print_pressed_timeout = null;
            }

            this._print_pressed_timeout = setTimeout(() => {
                this._PrintTicketModal();

                this._print_ignore_selected = true;
                setTimeout(() => {  // only ignore on current loop
                    this._print_ignore_selected = false;
                }, 0);                
            }, 1000)
        }
        else {
            if (this._print_pressed_timeout){
                clearTimeout(this._print_pressed_timeout);
                this._print_pressed_timeout = null;
            }
        }
    }

    InvoiceSend(){
        this._ticket.DoEmail();

        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);
    }

    TicketInvoice(){
        this.Action = 'invce';
        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);
    }

    OnInvoiceDone(success){
        if (success){
            this.Action = 'view';   // completed
        }
        else {
            this.CancelAction();    // cancelled
        }
    }

    private async _TicketCancel(){
        if (!this._place.AllowTicketCancel(this._user)){
            return;     // re-check en la acción (bloqueo/cambio de camarero con la
                        // confirmación de cancelación ya abierta)
        }
        this._ticket.DoCancel();

        // recalculate and commit ticket changes
        this._ticket.DoUpdate();
        this._AddRefundRequests();
        this._ticket.DoRefresh('TICKET');

        await this._ticket.DoCommit();
        this.toast.ShowAlert('danger', this.lang.tr('@place_ticket_cancelled_toast'), 2000);

        this._onAction.emit('CANCEL');

        if (!this._ticket.IsValid){
            this.OnClose();
        }
    }

    async TicketCancel(){
        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);

        let alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_cancel_ticket_confirm_tittle'),
            message: this.lang.tr('@place_cancel_ticket_confirm_message'),
            backdropDismiss: false,
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then((detail:any) => {
            if (detail && detail.data){
                this._TicketCancel();
            }
        });

        await alert.present();
    }

    private _onshowlast = false;
    get ShowLast(){
        return this._onshowlast;
    }

    set ShowLast(value){
        this._onshowlast = value;
        this.change.markForCheck();
    }

    OnLast(){
        this.ShowLast = !this.ShowLast;
    }

    get LastTickets(){
        return this._place?.GetLastTickets() || [];
    }

    /************************/
    /* TICKET RETURNS       */
    /************************/

    private get HasSelectedProducts(){
        let _products = [];

        _products = _products.concat(this._ticket.ProductsToRefund);
        _products = _products.concat(this._ticket.ProductsToPrepare);
        _products = _products.concat(this._ticket.ProductsDelivered);

        for(let _product of _products){
            let _some = _product.product.requested.some(
            requested => {
                return (requested['isSelected'] == true);
            });

            if (_some){
                return true;
            }
        }

        return false;
    }

    private get AllSelectedProducts(){
        let _products = [];

        _products = _products.concat(this._ticket.ProductsToRefund);
        _products = _products.concat(this._ticket.ProductsToPrepare);
        _products = _products.concat(this._ticket.ProductsDelivered);

        for(let _product of _products){
            let _every = _product.product.requested.every(
            requested => {
                return (requested['isSelected'] == true);
            });

            if (!_every){
                return false;
            }
        }

        return true;
    }

    TicketReturn(){
        this.Action = 'retrn';
        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);
    }

    get CanDoReturn(){
        return this.HasSelectedProducts;
    }

    private _AddRefundRequests(){
        if (this._ticket.ToRefund){
            let _askwaiter = this._ticket.request;
            if (!_askwaiter){
                _askwaiter = new AskWaiter(null, this.data);
            }

            if (_askwaiter){
                _askwaiter.ticket = this._ticket;
                _askwaiter.status = 'AC';
                _askwaiter.reason = 'REFUND';
                _askwaiter.amount = Number(this._ticket.ToRefund);        
            }

            this._ticket.request = _askwaiter;
        }
    }

    async OnTicketReturn(){
        if (!this._place.AllowTicketReturn(this._user)){
            return;     // re-check en la acción (devolución) con el usuario efectivo
        }
        // Bloqueo anti doble-click COMPARTIDO con OnTicketRefund: esta via abona
        // por el datafono (CardRefund) esperando a dialogos/cajon, y su boton no
        // se deshabilita. Sin este guard, un 2o click reentra y lee el MISMO alias
        // cacheado antes de que el settleCharge del final lo liquide -> 2o
        // CardRefund (doble abono). Se libera SIEMPRE en el finally.
        if (this._refunding){
            return;
        }
        // Bloquear YA, antes del primer await, y notificar a OnPush (ver
        // OnTicketRefund): si no, el botón seguía activo mientras el cajón actuaba.
        this._refunding = true;
        this.change.markForCheck();
        // Reserva cross-contexto de TODA la devolucion de ESTE ticket ANTES de
        // tocar hardware: el tramo mixto dispensa efectivo (CASH_OUT) antes de
        // llegar al abono de tarjeta, y `_refunding` es solo por instancia, asi
        // que sin esto dos vistas/pestañas podrian dispensar el mismo efectivo
        // antes de que ninguna reservara el cargo del tramo tarjeta. Clave = uuid
        // del ticket (namespace propio, no colisiona con el order_id del cargo).
        let _returnRef = (this._ticket as any)._uuid;
        // La reserva va ANTES del try/finally: si lanzara, _refunding quedaría a
        // true y el botón bloqueado hasta recrear la vista. Se trata como "no
        // reservado" y se rehabilita.
        let _claimed = false;
        try {
            _claimed = !_returnRef || await this.cashlessStore.claimCharge(_returnRef, null);
        }
        catch(e){
            console.error('[REFUND] claimCharge falló; no se inicia la devolución', e);
        }
        if (!_claimed){
            this.toast.ShowAlert('warning', this.lang.tr('@cashless_refund_inprogress'));
            this._refunding = false;
            this.change.markForCheck();
            return;
        }
        // Por defecto se libera la reserva al salir (finally). Solo se RETIENE si
        // el commit final falla tras haber dispensado hardware (ver mas abajo),
        // para que un reintento no vuelva a dispensar/abonar los mismos productos.
        let _committed = true;
        try {
        // Reparto de la devolucion MIXTA por metodo con los importes tecleados
        // en el ReturnForm. Orden money-safe (evita estados parciales):
        //   0) pre-resolvemos el terminal de tarjeta ANTES de dispensar nada —
        //      un cancel del picker aborta sin que salga dinero.
        //   1) EFECTIVO primero (CASH_OUT del cajon): si falla, abortamos antes
        //      de tocar la tarjeta -> no queda dinero fuera sin registrar.
        //   2) TARJETA (paytef) despues: si falla con el efectivo YA dispensado
        //      NO abortamos (el efectivo salio y hay que registrarlo); avisamos
        //      y NO contabilizamos la tarjeta (queda pendiente manual).
        // Sin hardware para un metodo -> se registra tal cual (manual).
        // El desglose contable (TICKETMIXEDPAY) se ajusta abajo SOLO por el
        // metodo que realmente se dispenso -> billetes fuera = arqueo.
        let _cardRefund = 0;
        let _cashRefund = 0;
        let _cashDone = true;   // efectivo dispensado (o sin cajon / sin importe)
        let _cardDone = true;   // tarjeta reintegrada (o manual / sin importe)
        if (this._ticket.payment == 'PAYMIXED'){
            _cardRefund = Number(this.ReturnForm.get('card').value) || 0;
            _cashRefund = Number(this.ReturnForm.get('cash').value) || 0;

            // 0) resolver terminal de tarjeta antes de dispensar nada.
            // Si aplica el abono por el datafono del cajon (CASHLOGY/PROSEGUR),
            // NO abrimos el picker de paytef: el tramo tarjeta se abona por el
            // cajon (paso 2). Antes se forzaba el picker y cancelarlo abortaba un
            // abono cashless perfectamente valido.
            let _cardTerminal = null;
            let _cardManual = false;
            if (_cardRefund > 0 && !this._cashlessRefundApplies()){
                let _terminals = resolvePaymentTerminals(this._place, this.view);
                if (_terminals.length > 0){
                    _cardTerminal = await pickPaymentTerminal(this.modalCtrl, this.lang, _terminals, this.view);
                    if (!_cardTerminal){
                        return;     // cancelo el picker -> nada dispensado
                    }
                    if (isPaymentTerminalManual(_cardTerminal)){
                        _cardManual = true;   // manual -> registrar tal cual
                    }
                } else {
                    _cardManual = true;       // sin terminal -> registrar tal cual
                }
            }

            // 1) EFECTIVO primero (cajon)
            if (_cashRefund > 0){
                let _cgInfo = this._place.smartdrawerForTicket(this._ticket);
                if (_cgInfo?.drawer && _cgInfo?.till){
                    _cashDone = await this._cashGuardCashOut(_cgInfo.till, _cgInfo.tolocal ?? false, _cashRefund);
                    if (!_cashDone){
                        // El cajon no dispenso: ofrecer devolucion manual (el
                        // operario entrega el efectivo a mano) o abortar antes de
                        // tocar la tarjeta -> asi no queda dinero fuera sin registrar.
                        if (!await this._askManualRefund()){
                            return;
                        }
                        _cashDone = true;   // manual: el efectivo se entrega a mano
                    }
                }
                // sin cajon -> _cashDone = true (registrar tal cual)
            }

            // 2) TARJETA, con el efectivo ya confirmado
            if (_cardRefund > 0){
                // Cajon con datafono (PROSEGUR/CASHLOGY): abonar la tarjeta por
                // el datafono, referenciado al cobro original, en vez de
                // paytef/manual. resolvePaymentTerminals (paytef-only) no ve esos
                // cajones, por eso el tramo tarjeta caia siempre a "manual" con un
                // cajon cashless (finding Copilot). Reutiliza el mismo helper que
                // la devolucion NO mixta, con el importe del tramo tarjeta.
                let _cashlessCard = await this._runCashlessRefundIfAvailable(_cardRefund);
                if (_cashlessCard === true){
                    _cardDone = true;   // abonado por el datafono (o manual elegido)
                }
                else if (_cashlessCard === null){
                    // Intentado por el datafono pero fallo/cancelado. El efectivo
                    // YA salio: NO abortamos; la tarjeta queda pendiente de abono
                    // manual (el helper ya avisa en caso de fallo).
                    _cardDone = false;
                }
                else if (!_cardManual && _cardTerminal){
                    // false = sin cajon cashless -> flujo paytef de siempre.
                    _cardDone = await this._openPaytefRefundModal([ this._ticket ], _cardTerminal, _cardRefund);
                    if (!_cardDone){
                        // el efectivo YA salio: NO abortamos; avisamos y la parte
                        // de tarjeta no se contabiliza (queda pendiente manual).
                        this.toast.ShowAlert('danger', this.lang.tr('@paytef_refund_failed'));
                    }
                }
            }
        }

        let _products = [];

        _products = _products.concat(this._ticket.ProductsToRefund);
        _products = _products.concat(this._ticket.ProductsToPrepare);
        _products = _products.concat(this._ticket.ProductsDelivered);

        let _ticketproducts = [];   // select the products to return
        for(let _product of _products){
            for(let requested of _product.product.requested){
                if ((requested.status == 'AC') && (requested['isSelected'])){
                    _ticketproducts.push(requested);
                }
            }
        }        
        
        // En MIXTO, OnTicketReturn YA hizo el reintegro (efectivo/tarjeta) arriba,
        // asi que FINALIZAMOS aqui: 'DE' en vez de 'UN'. Si dejaramos 'UN' el
        // ticket quedaria con ToRefund y el boton de OnTicketRefund disponible ->
        // un 2o dispense proporcional = DOBLE salida de efectivo. Resto de
        // metodos: 'UN' si pagado (el paso de refund aparte sigue igual).
        let _finalizeMixedRefund = (this._ticket.payment == 'PAYMIXED');
        for(let _ticketproduct of _ticketproducts){
            _ticketproduct.status = (this._ticket.IsPaid && !this._ticket.IsToPay && !_finalizeMixedRefund) ? 'UN' : 'DE';

            if (!_ticketproduct.HasEvent('RD')){
                this._ticket.CartDel(_ticketproduct, false);
            }
        }

        // Ajuste contable del desglose mixto (TICKETMIXEDPAY): SOLO restamos el
        // importe del metodo que realmente se dispenso (_cashDone/_cardDone).
        // Si la tarjeta fallo tras dispensar el efectivo, no la restamos ->
        // queda como pagada (pendiente de reintegro manual) y el arqueo de caja
        // sigue cuadrando con los billetes que salieron.
        if (this._ticket.payment == 'PAYMIXED'){
            for(let _mixed of this._ticket.mixed){
                if (_mixed.payment == 'PAYCASH' && _cashDone){
                    _mixed.amount = _mixed.amount - _cashRefund;
                }

                if (_mixed.payment == 'PAYCARD' && _cardDone){
                    _mixed.amount = _mixed.amount - _cardRefund;
                }
            }

            setTimeout(() => {
                this._returnForm = null;   // clear the form
            }, 0);
        }

        // recalculate and commit ticket changes
        this._ticket.DoUpdate(true);
        // En MIXTO ya reintegramos y finalizamos (productos 'DE' arriba): NO
        // creamos request de refund pendiente, para que el ticket no quede con
        // ToRefund y OnTicketRefund no pueda re-dispensar. Resto de metodos:
        // como antes (marca 'UN' y el refund se procesa en su paso aparte).
        if (this._ticket.payment != 'PAYMIXED'){
            this._AddRefundRequests();
        }

        // delete the ticket if it has no products
        if (this._ticket.IsEmpty){  
            this._ticket.DoCancel();
        }

        // AWAIT del commit ANTES de liberar el guard (finally): si se dejara en
        // fire-and-forget (.then), el `_refunding` se liberaría con la
        // persistencia aun en vuelo y un 2o click reentraria con el estado del
        // ticket sin guardar (ToRefund todavia presente) -> 2a devolucion de los
        // mismos productos. Emitimos 'RETURN' tras el await.
        // DoCommit devuelve false si falla la preparacion o el commit en backend
        // (ver ticket.ts). Si YA se dispenso hardware (efectivo/tarjeta) y la
        // persistencia falla, NO liberamos la reserva: dejamos la devolucion como
        // INDETERMINADA para que un reintento no vuelva a dispensar/abonar los
        // mismos productos; la reserva expira por TTL y queda para reconciliar.
        _committed = (await this._ticket.DoCommit()) !== false;
        if (_committed){
            this._onAction.emit('RETURN');
        }
        else {
            this.toast.ShowAlert('danger', this.lang.tr('@cashless_return_commit_failed'));
        }

        if (this._ticket.IsValid){
            this.Action = 'view';
        }
        else {
            this.OnClose();
        }
        } finally {
            this._refunding = false;
            this.change.markForCheck();     // OnPush: re-pintar el botón rehabilitado
            // Liberar la reserva solo si el commit fue OK; si fallo tras dispensar
            // hardware, se RETIENE para bloquear reintentos (expira por TTL).
            if (_returnRef && _committed){ await this.cashlessStore.releaseCharge(_returnRef, null); }
        }
    }

    async OnTicketRefund(){
        // Bloqueo anti doble-click: el cajon tarda en dispensar/abonar. Si no
        // se bloquea, un segundo click reentra y manda una 2a devolucion al
        // cajon. Se bloquea al entrar y se rehabilita SIEMPRE al terminar
        // (exito, fallo o cancelacion) via finally -> el operario reintenta.
        if (this._refunding){
            return;
        }
        // Bloquear YA, antes del primer await (la reserva de abajo es asíncrona:
        // un 2º toque en esa ventana entraba con _refunding aún a false). La vista
        // es OnPush: sin markForCheck el botón seguía verde y sin spinner mientras
        // el cajón (Prosegur) dispensaba, aunque el guard lógico ya lo frenara.
        this._refunding = true;
        this.change.markForCheck();
        // Reserva cross-contexto de TODA la devolucion de ESTE ticket ANTES de
        // tocar hardware (el tramo mixto dispensa efectivo): `_refunding` es solo
        // por instancia, asi que sin esto dos vistas/pestañas podrian dispensar el
        // mismo efectivo. Mismo patron que OnTicketReturn (clave = uuid ticket).
        let _returnRef = (this._ticket as any)._uuid;
        // La reserva va ANTES del try/finally: si lanzara, _refunding quedaría a
        // true y el botón bloqueado hasta recrear la vista. Se trata como "no
        // reservado" y se rehabilita.
        let _claimed = false;
        try {
            _claimed = !_returnRef || await this.cashlessStore.claimCharge(_returnRef, null);
        }
        catch(e){
            console.error('[REFUND] claimCharge falló; no se inicia la devolución', e);
        }
        if (!_claimed){
            this.toast.ShowAlert('warning', this.lang.tr('@cashless_refund_inprogress'));
            this._refunding = false;
            this.change.markForCheck();
            return;
        }
        // Se libera la reserva al salir (finally). Solo se RETIENE si el commit
        // final falla tras dispensar hardware, para bloquear reintentos.
        let _committed = true;
        try {
        // Paytef leg: only when the original payment was by card and the
        // place has an active paytef terminal. For cash/transfer/etc. we
        // fall through to the existing refund flow — no terminal traffic.
        // OJO MIXTO: el tramo tarjeta del PAYMIXED NO va aqui, sino DESPUES de
        // dispensar el efectivo (mas abajo): el MDW de Prosegur no puede
        // dispensar efectivo justo tras un CardRefund (el Dispense se cuelga),
        // asi que el orden money-safe es efectivo primero, tarjeta despues.
        if (this._ticket.payment == 'PAYCARD'){
            let _refundOk = await this._runPaytefRefundIfAvailable();
            if (_refundOk === null){
                return;     // user cancelled the paytef flow mid-way — keep ticket as-is
            }
            // null is cancel; true/false continue to the local refund
            // regardless (true = terminal refund succeeded, false = no
            // terminal or user chose manual refund)
        }

        // remove the refund request
        if (this._ticket.request && this._ticket.request.reason == 'REFUND'){
            this._ticket.request.status = 'DE';
        }

        // Calculate refund amount BEFORE changing product statuses
        // Use ToRefund if available, otherwise calculate manually or use ticket price
        let _totalRefundAmount = 0;
        if (this._ticket.ToRefund){
            _totalRefundAmount = parseFloat(this._ticket.ToRefund);
        } else if (this._ticket.IsPaid){
            // If ToRefund is not available, try to calculate it
            // First, update the ticket to ensure refund is calculated
            this._ticket.DoUpdate(true);
            if (this._ticket.ToRefund){
                _totalRefundAmount = parseFloat(this._ticket.ToRefund);
            } else {
                // Fallback: use ticket price if it's a full refund
                _totalRefundAmount = this._ticket.price || 0;
            }
        }

        // Process CashGuard refund only if payment was in cash (BEFORE updating statuses)
        let _cashRefundAmount = 0;
        let _cashRefundBlocked = false;   // true si hay cajon y el dispense NO confirma → no committear
        if (this._ticket.payment == 'PAYCASH' || this._ticket.payment == 'PAYMIXED'){
            console.log('[CASHGUARD REFUND] Payment type:', this._ticket.payment);
            console.log('[CASHGUARD REFUND] Total refund amount:', _totalRefundAmount);
            
                // Calculate cash refund amount
                if (this._ticket.payment == 'PAYCASH'){
                    _cashRefundAmount = _totalRefundAmount;
                } else if (this._ticket.payment == 'PAYMIXED'){
                    // For mixed payments, calculate the cash portion of the refund
                    let _cashPaid = this._ticket.payAmount('PAYCASH');
                    let _totalPaid = this._ticket.price;
                    if (_totalPaid > 0 && _cashPaid > 0){
                        _cashRefundAmount = Math.min(_totalRefundAmount, (_cashPaid / _totalPaid) * _totalRefundAmount);
                    }
                }

                console.log('[CASHGUARD REFUND] Cash refund amount:', _cashRefundAmount);

                // Process CashGuard refund if there's cash amount to refund
                if (_cashRefundAmount > 0){
                    let _cgInfo = this._place.smartdrawerForTicket(this._ticket);
                    let _smartdrawer = _cgInfo?.drawer || null;
                    let _cgTill = _cgInfo?.till || null;
                    let _tolocal = _cgInfo?.tolocal ?? false;
                    // Target del WS = modelo del cajon (CASHGUARD/CASHLOGY).
                    let _target = _smartdrawer?.model || 'CASHGUARD';

                console.log('[CASHGUARD REFUND] Smart drawer found:', _smartdrawer ? 'YES' : 'NO');

                if (_smartdrawer && _cgTill){
                    // Hay cajon: el reintegro solo es valido si la maquina
                    // dispensa. Pesimista por defecto; se libera abajo unicamente
                    // si el CASH_OUT confirma SUCCESS.
                    _cashRefundBlocked = true;
                    try {
                        let _host = _cgTill.wshost;
                        let _port = _cgTill.wsport ? parseInt(_cgTill.wsport) : 5005;
                        let _name = _cgTill.name;

                        console.log('[CASHGUARD REFUND] Connecting to:', _host, _port, _name);
                        let _connection = await this.devsvc.Connect(_host, _port, _name, _tolocal);
                        if (_connection){
                            try {
                                let statusResponse = await _connection.Send(_target, {
                                    command: 'STATUS'
                                });
                                
                                console.log('[CASHGUARD REFUND] Status response:', statusResponse);
                                
                                if (statusResponse && statusResponse.exec == 'SUCCESS'){
                                    let _transactionId = Math.floor(Math.random() * 1000000);
                                    let _amountCents = Math.round(_cashRefundAmount * 100);
                                    
                                    let _cashOutCommand = {
                                        command: 'CASH_OUT',
                                        transaction_id: _transactionId,
                                        amount_due: _amountCents
                                    };
                                    
                                    console.log('[CASHGUARD REFUND] Sending CASH_OUT command:', _cashOutCommand);
                                    
                                    let cashOutResponse = await _connection.Send(_target, _cashOutCommand);
                                    
                                    console.log('[CASHGUARD REFUND] Cash out response:', cashOutResponse);
                                    
                                    if (cashOutResponse && cashOutResponse.exec == 'SUCCESS'){
                                        _cashRefundBlocked = false;   // dispensado OK → se permite marcar devuelto
                                        this.toast.ShowAlert('success', this.lang.tr('@cashguard_cashout_success'), 2000);
                                    } else {
                                        this.toast.ShowAlert('warning', this.lang.tr('@cashguard_cashout_error'), 3000);
                                    }
                                } else {
                                    console.warn('[CASHGUARD REFUND] Status check failed:', statusResponse);
                                }
                            } catch(e){
                                console.error('[CASHGUARD REFUND] Error processing refund:', e);
                                this.toast.ShowAlert('warning', this.lang.tr('@cashguard_cashout_error'), 2000);
                            } finally {
                                this.devsvc.Disconnect(_connection);
                            }
                        } else {
                            console.warn('[CASHGUARD REFUND] Connection failed');
                        }
                    } catch(e){
                        console.error('[CASHGUARD REFUND] Error connecting for refund:', e);
                    }
                }
            } else {
                console.log('[CASHGUARD REFUND] No cash amount to refund');
            }
        } else {
            console.log('[CASHGUARD REFUND] Payment was not in cash or no refund amount, skipping CashGuard refund');
        }

        // Gate de integridad de caja: si habia cajon y el dispense fisico NO
        // confirmo (STATUS/CASH_OUT != SUCCESS, conexion fallida o excepcion),
        // NO marcamos el ticket como devuelto ni committeamos — el dinero no
        // ha salido de la maquina. El operario reintenta o hace la devolucion
        // manual. Refund manual sin cajon: _cashRefundBlocked queda false.
        if (_cashRefundBlocked){
            // El cajon no dispenso: ofrecer devolucion manual (el operario
            // entrega el efectivo a mano) o abortar. Si continua en manual,
            // seguimos y registramos la devolucion (mismo arqueo: el dinero
            // salio); si aborta, no marcamos devuelto ni committeamos.
            if (!await this._askManualRefund()){
                return;
            }
        }

        // Tramo TARJETA del PAYMIXED: DESPUES del efectivo (ver nota arriba: el
        // MDW de Prosegur no dispensa bien justo tras un CardRefund). Abona solo
        // la porcion de tarjeta (total - porcion efectivo, ya calculadas). El
        // efectivo ya salio: si el abono a tarjeta se cancela/falla NO abortamos,
        // queda pendiente de abono manual — mismo criterio que OnTicketReturn.
        if (this._ticket.payment == 'PAYMIXED'){
            let _cardPortion = Math.max(0, _totalRefundAmount - _cashRefundAmount);
            if (_cardPortion > 0){
                let _cardResult = await this._runPaytefRefundIfAvailable(_cardPortion);
                // true = abonado por datafono O el operario eligio manual. null =
                // cancelado; false = sin terminal/no aplica. En esos dos ultimos
                // el efectivo YA salio (no abortamos), pero AVISAMOS de que el
                // tramo tarjeta queda pendiente de abono manual -> no silencioso.
                if (_cardResult === null || _cardResult === false){
                    this.toast.ShowAlert('warning', this.lang.tr('@refund_card_pending_manual', [ this.Price(_cardPortion), this.lang.tr('@currency') ]), 4000);
                }
            }
        }

        // update status for refund pending items
        for(let _ticketproduct of this._ticket.products){
            if (_ticketproduct.status == 'UN'){ 
                _ticketproduct.status = 'CC';   // mark the product cancelation    
                _ticketproduct.status = 'DE';   // mark the product removal
            }
        }

        for(let _ticketoffer of this._ticket.offers){
            if (_ticketoffer.status == 'UN'){ 
                _ticketoffer.status = 'DE';    
            }
        }

        for(let _ticketextra of this._ticket.extras){
            if (_ticketextra.status == 'UN'){ 
                _ticketextra.status = 'DE';    
            }
        }
        
        let _hasProducts = this._ticket.products.some(_ticketproduct => {
            return _ticketproduct.status != 'DE';
        });

        // remove the discounts when empty
        if (!_hasProducts){
            for (let _discount of this._ticket.discounts){
                _discount.status = 'DE';
            }
        }

        this._ticket.DoUpdate();
        
        // delete the ticket if it has no products
        if (!_hasProducts){  
            this._ticket.DoCancel();
            this.OnClose();
        }

        // Esperar al commit ANTES de liberar el guard: DoCommit encadena el
        // dispense/abono del cajon y es async. Con .then() (fire-and-forget) el
        // finally liberaba _refunding de inmediato y un 2o click reentraba
        // mientras la primera devolucion seguia en curso (doble abono).
        _committed = (await this._ticket.DoCommit()) !== false;
        if (_committed){
            this._onAction.emit('REFUND');
        }
        else {
            // Commit fallido tras dispensar hardware: no liberar la reserva (que
            // expire por TTL) para bloquear reintentos que re-dispensen/re-abonen.
            this.toast.ShowAlert('danger', this.lang.tr('@cashless_return_commit_failed'));
        }
        }
        finally {
            // Rehabilitar el boton pase lo que pase, incluidos los early
            // return de arriba (cancelacion paytef / abort de refund manual).
            this._refunding = false;
            this.change.markForCheck();     // OnPush: re-pintar el botón rehabilitado
            // Liberar la reserva solo si el commit fue OK; si fallo tras dispensar
            // hardware, se RETIENE para bloquear reintentos (expira por TTL).
            if (_returnRef && _committed){ await this.cashlessStore.releaseCharge(_returnRef, null); }
        }
    }

    /************************/
    /* MERGE TICKET         */
    /************************/

    get MergeTickets(){
        let _tickets = [];
        for(let _ticket of this._place.tickets){
            if ((_ticket != this._ticket) && (_ticket.qrcode == this._ticket.qrcode) && (_ticket.IsValid) && (_ticket.IsRecent) && (['AC', 'RD'].indexOf(_ticket.status) != -1) && !(_ticket.IsPaid || _ticket.IsToPay)){
                _tickets.push(_ticket);
            }
        }
        return _tickets;
    }

    TicketMerge(){
        this.Action = 'merge';
        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);
    }

    public _mergeinfo = null;
    async OnMergeCancel(){
        if (!this._mergeinfo){
            return;
        }
        
        let _mergeinfo = this._mergeinfo;
        this._mergeinfo = null;

        if (_mergeinfo){
            // remove the merged products
            for(let _ticketproduct of _mergeinfo.tktprd){
                this._ticket.DelProduct(_ticketproduct);
                _ticketproduct.ticket = _mergeinfo.target;
            }

            // remove the merged discounts
            for (let _ticektdiscount of _mergeinfo.tktdct.toDelete){
                this._ticket.AddDiscount(_ticektdiscount);
            }

            for (let _ticektdiscount of _mergeinfo.tktdct.toInsert){
                this._ticket.DelDiscount(_ticektdiscount);
                _ticektdiscount.ticket = _mergeinfo.target;
            }

            // recalculate ticket changes
            this._ticket.isopen = true;
            this._ticket.DoUpdate();
            this._ticket.isopen = false;    
        }

        this.CancelAction();
    }

    async OnMergeCommit(){
        let _mergeinfo = this._mergeinfo;
        this._mergeinfo = null;

        if (_mergeinfo){
            let _merge = _mergeinfo.target;

            // add changes information to ticket
            for(let _ticketproduct of _mergeinfo.tktprd){
                _merge.DelProduct(_ticketproduct);
                _ticketproduct.ticket = this._ticket;
                this._ticket.AddProduct(_ticketproduct);
            }

            // apply ticket discounts
            for (let _ticketdiscount of _mergeinfo.tktdct.toDelete){
                _ticketdiscount.status = 'DE';
            }

            for (let _ticketdiscount of _mergeinfo.tktdct.toInsert){
                _merge.DelDiscount(_ticketdiscount);
                _ticketdiscount.ticket = this._ticket;
                this._ticket.AddDiscount(_ticketdiscount);
            }

            for (let _ticketdiscount of _mergeinfo.target.discounts){
                _merge.DelDiscount(_ticketdiscount);
            }

            // cancel the merged ticket (it should be empty)
            _merge.DoCancel();

            // recalculate and commit ticket changes
            let _transaction = await this.data.BeginTransaction();
            if (_transaction){
                for(let _ticket of [this._ticket, _merge]){
                    _ticket.isopen = true;
                    _ticket.DoUpdate();
                    _ticket.isopen = false;

                    _transaction.SendCommit(_ticket);
                }

                _transaction.Flush(true).then(
                data => {
                    this._onAction.emit('MERGE');
                });
            }
        }

        this.Action = 'view';   // completed
    }

    OnTicketMerge(_merge){
        let _products = [];

        _products = _products.concat(_merge.ProductsToRefund);
        _products = _products.concat(_merge.ProductsToPrepare);
        _products = _products.concat(_merge.ProductsDelivered);

        let _ticketproducts = [];   // select all the ticket products
        for(let _product of _products){
            for(let requested of _product.product.requested){
                _ticketproducts.push(requested);
            }
        }
        
        let _ticketdiscounts = {
            toInsert: [],       // discounts to be added to the ticket (for price calculation)
            toDelete: []        // discounts to be removed from the ticket
        }

        let _discountwarning = this._MergeWarning(this._ticket, _merge);
        if (_discountwarning){
            for (let _ticketdiscount of this._ticket.discounts){
                _ticketdiscounts.toDelete.push(_ticketdiscount);;
            }
        }
        else {
            for(let _ticketdiscount of _merge.discounts){
                let _applied = this._ticket.discounts.some(
                _discount => {
                    return (_discount.discount == _ticketdiscount.discount);
                });

                if (!_applied){
                    _ticketdiscounts.toInsert.push(_ticketdiscount);
                }
            }
        }

        // complete the merge information
        this._mergeinfo = {
            tktprd: _ticketproducts,
            tktdct: _ticketdiscounts,
            dscwrn: _discountwarning,
            target: _merge,
            status: _merge.status
        };

        // add the selected products to current (selected will be cancelled)
        for(let _ticketproduct of _ticketproducts){
            _ticketproduct.ticket = this._ticket;
            this._ticket.AddProduct(_ticketproduct);
        }

        // add the selected discounts to current (selected will be cancelled)
        for (let _ticketdiscount of _ticketdiscounts.toDelete){
            this._ticket.DelDiscount(_ticketdiscount);
        }

        for (let _ticketdiscount of _ticketdiscounts.toInsert){
            _ticketdiscount.ticket = this._ticket;
            this._ticket.AddDiscount(_ticketdiscount);
        }

        // recalculate ticket changes
        this._ticket.isopen = true;
        this._ticket.DoUpdate();
        this._ticket.isopen = false;
    }

    private _MergeDiscounts(_ticket1, _ticket2){
        let _d1 = _ticket1?.discounts || [];
        let _d2 = _ticket2?.discounts || [];

        let _discounts = new Set <Discount> ();
        for (let _discount of _d1.concat(_d2)){
            if (_discount.IsValid){
                _discounts.add(_discount.discount);
            }
        }
        return Array.from(_discounts);
    }

    private _MergeWarning(_ticket1, _ticket2){
        if (!_ticket1 || !_ticket2){
            return false;
        }

        let _discounts = this._MergeDiscounts(_ticket1, _ticket2);
        if (_discounts.length < 2){
            return false;
        }

        let _incompatible = _discounts.some(_discount => {
            return (_discount.cumulative == false);    // more than one discount and at least one is not cumulative
        })

        return _incompatible;
    }

    get MergeWarning(){
        if (this._mergeinfo){
            return this._mergeinfo.dscwrn;
        }

        return this._MergeWarning(this._ticket, this._mergeinfo?.target || null);
    }

    /************************/
    /* SPLIT TICKET         */
    /************************/

    TicketSplit(){
        this.Action = 'split';
        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);
    }

    async OnTicketSplit(){
        let _products = [];

        _products = _products.concat(this._ticket.ProductsToRefund);
        _products = _products.concat(this._ticket.ProductsToPrepare);
        _products = _products.concat(this._ticket.ProductsDelivered);

        let _ticketproducts = [];   // select the products to move
        for(let _product of _products){
            for(let requested of _product.product.requested){
                if (requested['isSelected']){
                    _ticketproducts.push(requested);
                }
            }
        }        

        // create a new ticket with the products
        let _split = new Ticket(null, this.data);
        if (_split) {
            _split.status = this._ticket.status;
            _split.session = this.data.session;
            _split.waiter = this.data.CurrentWaiter;
            _split.place = this._ticket.place;
            _split.qrcode = this._ticket.qrcode;

            this._place.AddTicket(_split);
        }

        // El ticket ORIGEN también lo edita este usuario (le saca productos),
        // así que se le atribuye igual que a la mitad nueva (último que edita).
        this._ticket.waiter = this.data.CurrentWaiter;

        // remove the products from current ticket
        for(let _ticketproduct of _ticketproducts){
            this._ticket.DelProduct(_ticketproduct);
            _ticketproduct.ticket = _split;
            _split.AddProduct(_ticketproduct);
        }

        // recalculate and commit ticket changes
        let _transaction = await this.data.BeginTransaction();
        if (_transaction){
            for(let _ticket of [this._ticket, _split]){
                _ticket.isopen = true;
                _ticket.DoUpdate(true);
                _ticket.isopen = false; 

                _transaction.SendCommit(_ticket);
            }

            _transaction.Flush(true).then(
            data => {
                this._onAction.emit('SPLIT');
            });
        }

        // show the new ticket (splitted ticket)
        this._onSplit.emit(_split);
        this.Action = 'view';   // completed
    }

    /************************/
    /* EDIT TICKET          */
    /************************/

    TicketModify(){
        let _tocart = this._ticket ? this._ticket.Copy() : null;
        if (_tocart){
            this._place.AddCart(_tocart);
        }

        this._onPanel.emit({
            main: 'cart',
            scnd: null,
            args: {
                ticket: _tocart
            }
        });

        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);
    }

    /************************/
    /* PAYMENT OPTIONS      */
    /************************/

    get ReturnPrice(){
        let _products = [];

        _products = _products.concat(this._ticket.ProductsToRefund);
        _products = _products.concat(this._ticket.ProductsToPrepare);
        _products = _products.concat(this._ticket.ProductsDelivered);

        let _return = 0;

        for(let _product of _products){
            for(let _requested of _product.product.requested){
                if (_requested['isSelected'] == true){
                    _return += _requested.charge;
                }
            }
        }

        return _return;
    }

    private ValidateReturnPrice(control: FormControl): any {
        let _price = Number(control.value);
        if (_price){
            if ((_price < 0) || (_price > this.ReturnPrice)){
                return { invalid: true };
            }
        }
        
        return null;
    }

    private MixedForm(){
        let _cash = null;
        let _card = null;

        let _validcash = [
            Validators.max(this.CashpayMaxPrice),
            Validators.required
        ]

        let _validcard = [
            Validators.required
        ]

        if(this._action == 'retrn'){
            _validcash.push(this.ValidateReturnPrice.bind(this))
            _validcard.push(this.ValidateReturnPrice.bind(this))
        }

        let _mixedform = new FormGroup({
            cash: new FormControl(_cash, _validcash),
            card: new FormControl(_card, _validcard)
        })

        return _mixedform;
    }

    private _paymentForm = null;
    get PaymentForm(){
        if (this._paymentForm == null){
            this._paymentForm = this.MixedForm();
        }
        return this._paymentForm;
    }

    private _returnForm = null;
    get ReturnForm(){
        if (this._returnForm == null){
            this._returnForm = this.MixedForm();
        }
        return this._returnForm;
    }

    TicketMixedPayment(){
        if (this.Action != 'mixed'){
            this.Action = 'mixed';
            setTimeout(() => {
                this.ShowOptions = false;
            }, 100);    
        }
    }

    OnPaymentCashChanged(){
        let _cash = this.PaymentForm.get('cash');
        let _card = this.PaymentForm.get('card');

        let _cashvalue = Number(_cash.value);
        if (_cashvalue <= this._ticket.price){
            let _cardvalue = this._ticket.price - _cashvalue; 
            if (_cashvalue){
                _cash.setValue(parseFloat(_cashvalue.toString().replace(",", ".")).toFixed(2));            
                _card.setValue(parseFloat(_cardvalue.toString().replace(",", ".")).toFixed(2))
            }
            else {
                _cash.setValue(null);
                _card.setValue(null);
            }    
        }
        else {
            _cash.setValue(parseFloat(_cashvalue.toString().replace(",", ".")).toFixed(2));            
        }
    }

    OnPaymentCardChanged(){
        let _cash = this.PaymentForm.get('cash');
        let _card = this.PaymentForm.get('card');

        let _cardvalue = Number(_card.value);
        if (_cardvalue <= this._ticket.price){
            let _cashvalue = this._ticket.price - _cardvalue; 
            if (_cardvalue){
                _card.setValue(parseFloat(_cardvalue.toString().replace(",", ".")).toFixed(2));            
                _cash.setValue(parseFloat(_cashvalue.toString().replace(",", ".")).toFixed(2))
            }
            else {
                _card.setValue(null);
                _cash.setValue(null);
            }    
        }
        else {
            _card.setValue(parseFloat(_cardvalue.toString().replace(",", ".")).toFixed(2));            
        }
    }

    OnReturnCashChanged(){
        let _return = this.ReturnPrice;

        let _cash = this.ReturnForm.get('cash');
        let _card = this.ReturnForm.get('card');

        let _cashvalue = _cash.value ? Number(_cash.value) : null;
        if (_cashvalue <= _return){
            let _cardvalue = _return - _cashvalue; 
            if (_cashvalue !== null){
                _cash.setValue(parseFloat(_cashvalue.toString().replace(",", ".")).toFixed(2));     
                if (_cardvalue <= this._ticket.payAmount('PAYCARD')){
                    _card.setValue(parseFloat(_cardvalue.toString().replace(",", ".")).toFixed(2))
                }
            }
            else {
                _cash.setValue(null);
                _card.setValue(null);
            }    
        }
        else {
            _cash.setValue(parseFloat(_cashvalue.toString().replace(",", ".")).toFixed(2));            
        }
    }

    OnReturnCardChanged(){
        let _return = this.ReturnPrice;

        let _cash = this.ReturnForm.get('cash');
        let _card = this.ReturnForm.get('card');

        let _cardvalue = _card.value ? Number(_card.value) : null;
        if (_cardvalue <= _return){
            let _cashvalue = _return - _cardvalue; 
            if (_cardvalue !== null){
                _card.setValue(parseFloat(_cardvalue.toString().replace(",", ".")).toFixed(2));            
                if (_cardvalue <= this._ticket.payAmount('PAYCASH')){
                    _cash.setValue(parseFloat(_cashvalue.toString().replace(",", ".")).toFixed(2))
                }
            }
            else {
                _card.setValue(null);
                _cash.setValue(null);
            }    
        }
        else {
            _card.setValue(parseFloat(_cardvalue.toString().replace(",", ".")).toFixed(2));            
        }
    }

    get PaymentReady(){
        let _total = 0;

        _total += Number(this.PaymentForm.get('cash').value);
        _total += Number(this.PaymentForm.get('card').value);

        return (_total == this._ticket.price);
    }

    get ReturnReady(){
        if (this._ticket.payment != 'PAYMIXED'){
            return true;
        }

        let _total = 0;

        _total += Number(this.ReturnForm.get('cash').value);
        _total += Number(this.ReturnForm.get('card').value);

        // Comparacion en centimos: sin esto, floats como 2.20+2.20+2.60 dan
        // 7.0000000001 y `_total == ReturnPrice` fallaba -> boton desactivado
        // aunque metalico+tarjeta cuadren con el total.
        return (Math.round(_total * 100) === Math.round(this.ReturnPrice * 100));
    }

    async OnMixedPayment(){
        try {
            this._ticket.payment = 'PAYMIXED';

        let _ticketcash = null;
        let _ticketcard = null;

        for (let _mixedpayment of this._ticket.mixed){
            if (_mixedpayment.payment == 'PAYCASH'){
                _ticketcash = _mixedpayment;
            }

            if (_mixedpayment.payment == 'PAYCARD'){
                _ticketcard = _mixedpayment;
            }
        }

        _ticketcash = _ticketcash || new TicketMixedPayment(null, this.data);
        let _cashAmount = Number(this.PaymentForm.get('cash').value);
        if (_ticketcash){
            _ticketcash.payment = 'PAYCASH',
            _ticketcash.amount = _cashAmount;
            _ticketcash.ticket = this._ticket;
        }

        _ticketcard = _ticketcard || new TicketMixedPayment(null, this.data);
        let _cardAmount = Number(this.PaymentForm.get('card').value);
        if (_ticketcard){
            _ticketcard.payment = 'PAYCARD',
            _ticketcard.amount = _cardAmount;
            _ticketcard.ticket = this._ticket;
        }

        // Orden money-safe (igual que OnTicketReturn): EFECTIVO primero. Si el
        // tramo efectivo se cancela/falla, abortamos ANTES de cobrar la tarjeta,
        // asi nunca queda un cargo en tarjeta ya aprobado sin venta (que habria
        // que abonar a mano; peor aun en un ticket nuevo sin orderno para
        // referenciar el abono). La tarjeta, que es lo caro de deshacer, va la
        // ULTIMA: si falla, no hay nada previo que revertir electronicamente.
        let _cashPaymentSuccess = true;
        if (_cashAmount > 0){
            _cashPaymentSuccess = await this._ProcessMixedCashPayment(_cashAmount);
        }
        if (!_cashPaymentSuccess){
            return; // Cash payment failed or was cancelled
        }

        let _cardPaymentSuccess = true;
        if (_cardAmount > 0){
            _cardPaymentSuccess = await this._ProcessMixedCardPayment(_cardAmount);
        }
        if (!_cardPaymentSuccess){
            // La tarjeta falló/canceló DESPUÉS de cobrar el efectivo por el
            // cajón: hay que DEVOLVER el efectivo ya depositado. Si no, el
            // dinero queda dentro sin atribuir (descuadre de arqueo) y un
            // reintento del pago mixto volvería a cobrar efectivo -> duplicado.
            // Solo si el efectivo entró REALMENTE en el cajón: en efectivo
            // manual / fallback (o sin cajón) un CASH_OUT dispensaría saldo
            // ajeno y el efectivo contado a mano nunca se devolvería.
            if (_cashAmount > 0 && _cashPaymentSuccess && this._mixedCashInDrawer){
                await this._returnMixedCash(_cashAmount);
            }
            return;
        }

        // Clear the form only after successful payment
        setTimeout(() => {
            this._paymentForm = null;   // clear the form
        }, 0);

        if (_ticketcash && _ticketcard){
            this._ticket.AddMixed(_ticketcash);
            this._ticket.AddMixed(_ticketcard);
        }

        this._place.DelCart(this._ticket);
        this._ticket.DoCommit().then(
        data => {
            this._onAction.emit('PAYMENT');
            this._onClose.emit();    
        });
        } catch(e){
            throw e;
        }
    }

    private async _ProcessMixedCardPayment(cardAmount: number): Promise<boolean>{
        let _cgInfo = this._place.smartdrawerForTicket(this._ticket);

        // Selector de datáfono UNIFICADO (igual que OnPaycard): con datáfonos de
        // varios tipos (PROSEGUR cashless + PAYTEF) o 2+ unidades, deja ELEGIR al
        // cajero en vez de ir directo al primero. Faltaba en el tramo TARJETA del
        // pago mixto -> iba directo al datáfono 1. Con un solo tipo devuelve
        // 'default' y sigue el flujo de siempre.
        if (this._waitmodal){ return false; }
        this._waitmodal = true;
        let _choice;
        try {
            _choice = await chooseCardTerminal(this.modalCtrl, this.lang, this._place, this.view, _cgInfo);
        } catch(e){
            // En el MIXTO el efectivo YA se cobró. Si el selector lanza (modal no se
            // pudo crear/presentar/cerrar), NO propagamos: OnMixedPayment solo hace
            // rethrow y se saltaría la devolución del efectivo. Devolvemos false para
            // que el tramo tarjeta "falle" limpio y se dispare la compensación.
            console.warn('[PAY MIXTO] selector de datáfono falló; se trata como fallo de tarjeta', e);
            return false;
        } finally {
            this._waitmodal = false;
        }
        if (_choice.kind !== 'default'){
            if (_choice.kind === 'cancelled'){ return false; }
            if (_choice.kind === 'manual'){
                if (!this._place.CardModal){ return true; }
                return this._openManualCardModal([ this._ticket ], cardAmount);
            }
            if (_choice.kind === 'paytef'){
                return this._openPaytefModal([ this._ticket ], _choice.terminal, cardAmount);
            }
            // prosegur: cobro cashless por el datáfono YA elegido (sin re-preguntar).
            if (this._waitmodal){ return false; }
            let _deps = this._cashlessDeps(cardAmount);
            _deps.chosenTerminal = _choice.terminal;
            this._waitmodal = true;
            let _r: boolean | null;
            try {
                _r = await tryCashlessCardPayment(this.devsvc, _deps, [ this._ticket ], _cgInfo);
            } finally {
                this._waitmodal = false;
            }
            return _r === null ? false : _r;
        }

        // default (un solo tipo de datáfono): flujo de siempre. El cajón con
        // datafono (PROSEGUR/CASHLOGY) cobra la tarjeta por él (CHARGE_CASHLESS)
        // con su importe explícito; si no puede, devuelve null y cae a paytef.
        if (this._waitmodal){
            return false;
        }
        this._waitmodal = true;
        let _cgCard: boolean | null;
        try {
            _cgCard = await tryCashlessCardPayment(this.devsvc, this._cashlessDeps(cardAmount), [ this._ticket ], _cgInfo);
        } finally {
            this._waitmodal = false;
        }
        if (_cgCard !== null){
            return _cgCard;
        }

        let _terminals = resolvePaymentTerminals(this._place, this.view);

        if (_terminals.length > 0){
            let _pick = await pickPaymentTerminal(this.modalCtrl, this.lang, _terminals, this.view);
            if (!_pick){
                return false;
            }
            if (isPaymentTerminalManual(_pick)){
                if (!this._place.CardModal){
                    return true;
                }
                return this._openManualCardModal([ this._ticket ], cardAmount);
            }
            return this._openPaytefModal([ this._ticket ], _pick, cardAmount);
        }

        if (this._place.CardModal){
            return this._openManualCardModal([ this._ticket ], cardAmount);
        }

        return true;
    }

    /**
     * Devuelve al cliente el efectivo ya cobrado por el cajón cuando la parte
     * TARJETA del pago mixto falla o se cancela. Sin esto, el efectivo queda
     * dentro sin atribuir (descuadre) y un reintento lo volvería a cobrar
     * (duplicado). Dispensa `cashAmount` por `CASH_OUT` (mismo mecanismo que el
     * reembolso). Si no hay cajón, o la devolución falla, avisa al cajero para
     * que lo resuelva a mano — nunca se traga el fallo en silencio.
     */
    private async _returnMixedCash(cashAmount: number): Promise<void> {
        let _cgInfo = this._place.smartdrawerForTicket(this._ticket);
        let _smartdrawer = _cgInfo?.drawer || null;
        let _cgTill = _cgInfo?.till || null;
        let _tolocal = _cgInfo?.tolocal ?? false;

        if (!_smartdrawer || !_cgTill){
            // No hay cajón físico que devolver: avisar para intervención manual.
            this.toast.ShowAlert('danger', this.lang.tr('@mixed_cash_return_manual'), 5000);
            return;
        }

        let _target = _smartdrawer.model || 'CASHGUARD';
        let _connection = null;
        let _ok = false;
        try {
            let _host = _cgTill.wshost;
            let _port = _cgTill.wsport ? parseInt(_cgTill.wsport) : 5005;
            _connection = await this.devsvc.Connect(_host, _port, _cgTill.name, _tolocal);
            if (_connection){
                let _resp = await _connection.Send(_target, {
                    command: 'CASH_OUT',
                    transaction_id: Math.floor(Math.random() * 1000000),
                    amount_due: Math.round(cashAmount * 100)
                });
                _ok = !!(_resp && _resp.exec == 'SUCCESS');
            }
        } catch(e){
            console.error('[PAY MIXTO] devolución de efectivo tras fallo de tarjeta:', e);
        } finally {
            if (_connection){
                this.devsvc.Disconnect(_connection);
            }
        }

        this.toast.ShowAlert(
            _ok ? 'warning' : 'danger',
            this.lang.tr(_ok ? '@mixed_cash_returned' : '@mixed_cash_return_manual'),
            _ok ? 3500 : 5000
        );
    }

    private async _ProcessMixedCashPayment(cashAmount: number): Promise<boolean>{
        // Por defecto el efectivo NO esta en el cajon; solo la rama de deposito
        // real en el cajon lo pone a true (ver mas abajo).
        this._mixedCashInDrawer = false;
        return new Promise<boolean>(async (resolve) => {
            let _cgInfo = this._place.smartdrawerForTicket(this._ticket);
            let _smartdrawer = _cgInfo?.drawer || null;
            let _cgTill = _cgInfo?.till || null;
            let _tolocal = _cgInfo?.tolocal ?? false;

            if (!_smartdrawer || !_cgTill){
                resolve(true);      // efectivo manual (sin cajon): _mixedCashInDrawer queda false
                return;
            }

            let modal;
            try {
                modal = await this.modalCtrl.create({
                    component: UiCashGuardCashPayModal,
                    cssClass: 'modal-cashguard',
                    backdropDismiss: false,
                    componentProps: {
                        tickets: [ this._ticket ],
                        device: _smartdrawer,
                        amountDue: cashAmount,
                        place: _cgTill,
                        tolocal: _tolocal
                    }
                });
            } catch(_e){
                console.error('[PAY] mixed cash modal error', _e);
                resolve(false);
                return;
            }

            modal.onDidDismiss().then(async (detail) => {
                if (detail.data && detail.data['fallback']){
                    let cashModal;
                    try {
                        cashModal = await this.modalCtrl.create({
                        component: UiCashPayModal,
                        cssClass: 'modal-cash',
                        backdropDismiss: false,
                        componentProps: { 
                            tickets: [ this._ticket ],
                            place: this._place
                        }                                    
                    });
                    } catch(_e){
                        console.error('[PAY] mixed fallback cash modal error', _e);
                        resolve(false);
                        return;
                    }
                    
                    cashModal.onDidDismiss().then((cashDetail) => {
                        if (cashDetail.data){
                            this._ticket.given = cashDetail.data['amount'];
                        }
                        resolve(cashDetail.data != null);
                    });
                    
                    try {
                        await cashModal.present();
                    } catch(e){
                        resolve(false);
                    }
                } else {
                    if (detail.data && detail.data['amount']){
                        this._ticket.given = detail.data['amount'];
                    }
                    let _deposited = detail.data != null && detail.data['amount'] != null;
                    // El efectivo entro en el cajon: si luego falla la tarjeta,
                    // _returnMixedCash puede devolverlo por CASH_OUT.
                    this._mixedCashInDrawer = _deposited;
                    resolve(_deposited);
                }
            });

            try {
                await modal.present();
            } catch(e){
                resolve(false);
            }
        });
    }

    /************************/
    /* PAY TO ACCOUNT       */
    /************************/

    private _acselect = null;
    get AccountSelect(){
        if (this._acselect == null){
            let _accounts = [];
            for(let _account of this._place.payaccounts){
                if (_account.IsValid && !_account.IsBlocked){
                    _accounts.push(_account);
                }
            }

            _accounts = _accounts.sort((a, b) => {
                if (a.name == b.name){
                    return 0;
                }

                return (a.name < b.name) ? -1 : 1;
            });

            let _options = [];
            for(let _account of _accounts){
                _options.push({
                    text: _account.name || this.lang.tr('@place_account_no_name'),
                    value: _account
                });    
            }

            this._acselect = _options;
        }

        return this._acselect;
    }

    public _account = null;

    OnTicketAccount(_account){
        this._account = _account;
    }

    OnPayAccount(_account){
        this._ticket.payment = 'PAYACCOUNT';
        this._ticket.account = _account;
        this._place.DelCart(this._ticket);

        this._ticket.DoCommit().then(
        data => {
            this._onAction.emit('ACCOUNT');
            this._onClose.emit();    
        });
    }

    TicketToAccount(){
        this._account = null;

        this.Action = 'account';
        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);
    }

    /************************/
    /* CHANGE TABLE         */
    /************************/

    private _tbselect = null;
    get TableSelect(){
        if (this._tbselect == null){
            let _tables = [ null ];
            for(let _table of this._place.tables){
                if (_table.IsValid && !_table.IsError){
                    _tables.push(_table);
                }
            }

            _tables = _tables.sort((a, b) => {
                if (!a || !b){
                    return !a ? -1 : 1;
                }
    
                return parseInt(a.number) - parseInt(b.number);
            });

            
            let _options = [];
            for(let _table of _tables){
                if (!_table){
                    _options.push({
                        text: this.lang.tr('@place_ticket_filter_table_bar'),
                        value: null
                    });
                }
                else {
                    _options.push({
                        text: this.lang.tr('@place_ticket_filter_table_number', [ _table.alias || _table.number ]),
                        value: _table
                    });    
                }
            }

            this._tbselect = _options;
        }

        return this._tbselect;
    }

    public _qrcode = null;
    
    async OnTicketTable(qrcode){
        if (qrcode != this._ticket.qrcode){
            let _oldtable = this._ticket.qrcode;
            if (_oldtable){
                _oldtable.DelTicket(this._ticket);
            }
            
            this._ticket.qrcode = qrcode;

            if (qrcode){
                qrcode.AddTicket(this._ticket);
            }

            this._ticket.isopen = true;
            this._ticket.DoUpdate();
            this._ticket.DoCommit().then(
            data => {
                this._onAction.emit('TABLE')
            });
            this._ticket.isopen = false;
    
            this.Action = 'view';   // completed    
        }
    }

    TicketTable(){
        this._qrcode = this._ticket.qrcode;

        this.Action = 'table';
        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);
    }

    /************************/
    /* REOPEN TICKET        */
    /************************/

    private async _OpenTicket(){
        if (!this._place.AllowTicketReopen(this._user)){
            return;     // re-check en la acción (reabrir) con el usuario efectivo
        }
        // perform any pending refund operation
        for(let _ticketproduct of this._ticket.products){
            if (_ticketproduct.status == 'UN'){ 
                _ticketproduct.status = 'DE';    
            }
        }

        for(let _ticketoffer of this._ticket.offers){
            if (_ticketoffer.status == 'UN'){ 
                _ticketoffer.status = 'DE';    
            }
        }

        for(let _ticketextra of this._ticket.extras){
            if (_ticketextra.status == 'UN'){ 
                _ticketextra.status = 'DE';    
            }
        }

        // remove the paid information from the products
        for(let _ticketproduct of this._ticket.products){
            if (_ticketproduct.status != 'DE'){ 
                _ticketproduct.DelEvent('RD');
                _ticketproduct.DelEvent('PP');
                _ticketproduct.DelEvent('PD');
            }
        }

        // remove any invoice information from the ticket
        if (this._ticket.invoice){
            this._ticket.invoice = null;
        }

        // remove any account information from the ticket
        if (this._ticket.account){
            this._ticket.account = null;
        }

        // modify the ticket source (now is from POS)
        if (this._ticket.session){
            this._ticket.session = this.data.session;
            this._ticket.waiter = this.data.CurrentWaiter;
        }

        this._ticket.payment = null;
        this._ticket.status = 'AC';

        // Marca de reapertura DELIBERADA: el server la exige para permitir el
        // PD->AC (su proteccion anti-stale-sync bloquea el reabrir salvo serie 'R',
        // que un reabrir reason 'X' no genera). Sin esto el ticket volvia a PD.
        this._ticket._reopenIntent = true;

        // Double-check de reabrir: ventana durante la cual un apply de datos
        // STALE (el server aun ve el ticket en PD porque el PHP todavia no ha
        // persistido este AC) NO puede revertir el AC local a PD ("se paga
        // solo"). Cubre el commit lento (observado ~10-17s) + el pull posterior.
        this._ticket._reopenGuardUntil = Date.now() + 30000;

        this._ticket.isopen = true;
        this._ticket.DoUpdate();
        this._ticket.DoCommit().then(
        data => {
            this._onAction.emit('REOPEN');
        });
        this._ticket.isopen = false;
    }

    async OpenTicket(){
        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);

        let alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_ticket_open_confirm'),
            message: this.lang.tr('@place_ticket_open_message'),
            backdropDismiss: false,
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then((detail:any) => {
            if (detail && detail.data){
                this._OpenTicket();
            }
        });

        await alert.present();
    }

    /************************/
    /* CHANGE PAYMENT       */
    /************************/

    async OnChangePayment(payment){
        this._ticket.account = null;
        this._ticket.payment = payment;

        if (this._ticket.account){
            this._ticket.account = null;
        }
        
        this._ticket.DoCommit().then(
        data => {
            this._onAction.emit('PAYMENT');
        });
        
        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);
    }
}
